import z, {
  object,
  boolean,
  literal,
  discriminatedUnion,
  ZodIssueCode,
  preprocess,
  coerce,
} from 'zod';

import { makeZodValidate as makeValidate } from '@occc-app/design-system';
import {
  IS_REQUIRED,
  IS_INTEGER,
  IS_POSITIVE,
  isRangeBetween,
} from '@occc-app/lib/constants/validationMessages';
import { dollarsToCents, centsToDollars } from '@occc-app/lib/currency';
import { MAX_32_BIT_INT, withIsEnabled } from '@occc/validation';
import {
  LimitDisabledSchemaWithIsEnabled,
  LimitSchema,
  validateMembershipLessThanGlobal,
} from '@occc/feat-limits';

import { BaseConfigurationSchema } from './base-configuration.schema';

import { AWARD_TYPES, TRANSACTION_DISCOUNT_TYPE } from '../types';

const AwardCountEnabledSchema = object({
  membership: LimitSchema,
  global: LimitSchema,
}).superRefine((data, ctx) => {
  validateMembershipLessThanGlobal({
    membership: data.membership,
    global: data.global,
    ctx,
  });
});

const AwardCountDisabledSchema = object({
  membership: preprocess(() => ({}), LimitDisabledSchemaWithIsEnabled),
  global: preprocess(() => ({}), LimitDisabledSchemaWithIsEnabled),
});

const AwardCountSchema = withIsEnabled(AwardCountEnabledSchema, AwardCountDisabledSchema);

const percentAmountSchema = coerce
  .number({ message: IS_REQUIRED })
  .int({ message: IS_INTEGER })
  .min(0, { message: isRangeBetween(0, 100) })
  .max(100, { message: isRangeBetween(0, 100) });

const dollarsAmountSchema = coerce.number({ message: IS_REQUIRED }).gt(0, { message: IS_POSITIVE });

const TransactionDiscountEarningMechanismSchema = discriminatedUnion('type', [
  object({
    type: literal(TRANSACTION_DISCOUNT_TYPE.PERCENT),
    amount: percentAmountSchema,
    purchaseAmountThreshold: dollarsAmountSchema,
    displayForOrderAhead: boolean().optional(),
  }),
  object({
    type: literal(TRANSACTION_DISCOUNT_TYPE.DOLLARS),
    amount: dollarsAmountSchema,
    purchaseAmountThreshold: dollarsAmountSchema,
    displayForOrderAhead: boolean().optional(),
  }),
]).superRefine((discriminantData, ctx) => {
  const addCentsOverflowIssue = (value: number | undefined, path: (string | number)[]) => {
    if (value === undefined) {
      return;
    }
    if (dollarsToCents(value) > MAX_32_BIT_INT) {
      ctx.addIssue({
        code: ZodIssueCode.custom,
        path,
        message: `This field should be less than or equal to $${centsToDollars(MAX_32_BIT_INT)}`, // MAX_32_BIT_INT / 100 for cents
      });
    }
  };

  if (discriminantData.type === TRANSACTION_DISCOUNT_TYPE.DOLLARS) {
    addCentsOverflowIssue(discriminantData.amount, ['amount']);
    addCentsOverflowIssue(discriminantData.purchaseAmountThreshold, ['purchaseAmountThreshold']);

    if (
      discriminantData.purchaseAmountThreshold !== undefined &&
      discriminantData.amount !== undefined &&
      discriminantData.purchaseAmountThreshold < discriminantData.amount
    ) {
      ctx.addIssue({
        code: ZodIssueCode.custom,
        path: ['purchaseAmountThreshold'],
        message: 'Purchase threshold cannot be lower than the discount amount',
      });
    }
  }

  if (discriminantData.type === TRANSACTION_DISCOUNT_TYPE.PERCENT) {
    addCentsOverflowIssue(discriminantData.purchaseAmountThreshold, ['purchaseAmountThreshold']);
  }
});

const TransactionDiscountSchema = BaseConfigurationSchema.extend({
  awardType: literal(AWARD_TYPES.TRANSACTION),
  transactionDiscount: preprocess((val) => val ?? {}, TransactionDiscountEarningMechanismSchema),
  awardCount: AwardCountSchema,
});

export type TransactionDiscountFormValues = z.infer<typeof TransactionDiscountSchema>;

export const validateTransactionDiscountForm = makeValidate(TransactionDiscountSchema);
